import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import { ButtonIconSquare, CancelButton } from '@/modules/base/components/buttons'
import { SignatureWizardSteps, addSignaturesToSigner, setActiveSigner, setActiveStep, setIsFinished } from '@/redux/ducks/signature/edit'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import ThemeInterface from '@/theme-interface'
import { useAppSelector } from '@/redux/store'
import { SIGNATURE_FIELD_TYPES } from '@/modules/base/util/constants'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  button: {
    width: 'unset',
  },
  backButton: {
    borderColor: theme.borderColors.default,
    backgroundColor: theme.white,
  },
}), { name: 'SignaturePlacementActions' })

const SignaturePlacementActions = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { activeStep, activeSigner, signers } = useAppSelector(state => state.signature.edit)
  const {
    pager: { setCurrentPage },
    items,
    setPlacingMode,
    resetFields,
    setCurrentItemIndex,
  } = useSignatureContext()

  const activeSignerSignatureItems = useMemo(() => {
    return items?.filter(item => (item.signerEmail === activeSigner.email))
  }, [activeSigner, items])

  const activeSignerIndex = useMemo(() => {
    if (!activeSigner) { return 0 }

    return signers.findIndex(signer => signer.email === activeSigner.email)
  }, [activeSigner])

  const formValid = useMemo(() => {
    return activeSignerSignatureItems.length && activeSignerSignatureItems.find((item) => item.type === SIGNATURE_FIELD_TYPES.SIGNATURE)
  }, [activeSignerSignatureItems])

  const handleContinue = (): void => {
    const { email } = activeSigner
    dispatch(addSignaturesToSigner({
      signerEmail: email,
      signatures: activeSignerSignatureItems,
    }))

    if (activeSignerIndex === (signers.length - 1)) {
      dispatch(setIsFinished(true))
      return
    }

    const newActiveSigner = signers[activeSignerIndex + 1]
    const newActiveSignatureIndex = items?.findIndex(signature => signature.signerEmail === newActiveSigner.email)

    if (newActiveSignatureIndex >= 0) {
      setCurrentItemIndex(newActiveSignatureIndex)
      setCurrentPage(items[newActiveSignatureIndex]?.position?.page)
    }

    dispatch(setActiveSigner({ signer: newActiveSigner }))
  }
  const handleBack = (): void => {
    if (activeSignerIndex > 0) {
      const newActiveSigner = signers[activeSignerIndex - 1]
      const newActiveSignatureIndex = items?.findIndex(signature => signature.signerEmail === newActiveSigner.email) ?? 0
      dispatch(setActiveSigner({ signer: newActiveSigner }))
      setCurrentItemIndex(newActiveSignatureIndex)
      setCurrentPage(items[newActiveSignatureIndex]?.position?.page)
      return
    }

    setPlacingMode(false)
    resetFields([])
    dispatch(setActiveStep({ step: SignatureWizardSteps.form }))
  }

  const backButton = [SignatureWizardSteps.signaturePlacement].includes(activeStep)
    ? (
      <CancelButton
        customClass={classes.backButton}
        label={lang.generics.back}
        onClick={handleBack}
      />
    )
    : null

  const continueButtonLabel = activeSignerIndex < (signers.length - 1)
    ? lang.signature.placement.button.nextSigner
    : lang.signature.placement.button.finish

  return (
    <div className={classes.root}>
      <div>
        {backButton}
      </div>
      <div>
        <ButtonIconSquare
          type="submit"
          label={continueButtonLabel}
          disabled={!formValid}
          customClass={classes.button}
          debounceTime={activeSignerIndex < (signers.length - 1) ? 0 : 1000}
          onClick={handleContinue} />
      </div>
    </div>
  )
}

export default SignaturePlacementActions
